import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, TextField, CircularProgress, Paper, List, ListItem, ListItemButton,
  Chip, Typography, InputAdornment, IconButton, Tooltip,
} from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import productoService, { PARAMS_PRODUCTO_OPERATIVO } from '../../../services/productoService';
import articuloXListaPrecioService from '../../../services/articuloXListaPrecioService';
import inventarioInformes from '../../../services/inventarioInformes';
import { resolverPrecioDesdeLista, obtenerPrecioBaseProducto } from '../../../utils/precioListaPedido';
import { formatCurrency } from '../../../utils/formatters';
import { getProductoFallbackImage, getProductoImageBySize } from '../../../utils/productImage';
import { esEntradaCodigoBarra } from '../../../utils/codigoBarraVenta';
import { getApiErrorMessage } from '../../../utils/apiError';
import { SuggestionItem } from './types';
import { aPrecioDisplay, multiplicadorIvaProducto } from './preciosIvaDisplay';

export type { SuggestionItem } from './types';

const PAGE_SIZE = 20;

const descripcionSx = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical' as const,
  overflow: 'hidden',
  lineHeight: 1.35,
  wordBreak: 'break-word' as const,
};

interface Props {
  nroLista?: string;
  disabled?: boolean;
  tipoIvaEmision?: string;
  preciosConIva?: boolean;
  onAgregarProducto: (item: SuggestionItem) => void;
}

const StockBadge: React.FC<{ stock: number | null | undefined }> = ({ stock }) => {
  if (stock == null) return null;
  if (stock <= 0)
    return <Chip label="Sin stock" size="small" color="error" sx={{ height: 18, fontSize: '0.65rem' }} />;
  if (stock < 10)
    return <Chip label={`Stock: ${stock}`} size="small" color="warning" sx={{ height: 18, fontSize: '0.65rem' }} />;
  return <Chip label={`Stock: ${stock}`} size="small" color="success" sx={{ height: 18, fontSize: '0.65rem' }} />;
};

const FacturaPOSSearchBar: React.FC<Props> = ({
  nroLista,
  disabled,
  tipoIvaEmision = '',
  preciosConIva = false,
  onAgregarProducto,
}) => {
  const [term, setTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [suggestions, setSuggestions] = useState<SuggestionItem[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [barcodeError, setBarcodeError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRequestIdRef = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listScrollRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);

  const enriquecerProductos = useCallback(async (items: any[]): Promise<SuggestionItem[]> => {
    return Promise.all(
      items.map(async (p) => {
        let precio = obtenerPrecioBaseProducto(p);

        if (nroLista) {
          try {
            const reg = await articuloXListaPrecioService.getArticuloXListaPrecio(
              nroLista,
              p.codigoArticulo,
            );
            const resultado = resolverPrecioDesdeLista(p, reg);
            if (resultado.tipo !== 'sinPrecio') precio = resultado.precioLista;
          } catch {
            /* sin precio en lista, usa precio base */
          }
        }

        let stock: number | null = null;
        try {
          const stockData = await inventarioInformes.getStockPorDeposito({
            codigoArticulo: p.codigoArticulo,
            pageSize: 50,
          });
          const rows: any[] = (stockData?.data ?? []).filter(
            (r: any) => (r.codigoArticulo ?? '').trim() === p.codigoArticulo.trim(),
          );
          if (rows.length > 0) {
            stock = rows.reduce(
              (acc: number, r: any) => acc + (Number(r.disponibleDeposito) || 0),
              0,
            );
          }
        } catch {
          /* sin stock disponible */
        }

        const multiplicador = preciosConIva
          ? multiplicadorIvaProducto(p, tipoIvaEmision)
          : 1;

        return {
          codigoArticulo: p.codigoArticulo,
          descripcion: p.descripcion,
          precioUnitario: precio,
          precioUnitarioDisplay: preciosConIva
            ? aPrecioDisplay(precio, multiplicador)
            : undefined,
          um: p.um ?? p.unidadMedida ?? '',
          stockDisponible: stock,
          imageUrl: getProductoImageBySize(p, 'thumbnail'),
          productoIva: p,
        };
      }),
    );
  }, [nroLista, preciosConIva, tipoIvaEmision]);

  const resetBusqueda = useCallback(() => {
    setSuggestions([]);
    setPageNumber(1);
    setTotalCount(0);
    setOpen(false);
    setHighlightedIndex(-1);
    itemRefs.current = [];
  }, []);

  const buscarProductos = useCallback(async (q: string) => {
    const trimmed = q.trim();
    if (!trimmed) {
      resetBusqueda();
      return;
    }

    const requestId = ++searchRequestIdRef.current;
    setLoading(true);
    setPageNumber(1);

    try {
      const res = await productoService.getProductos({
        searchTerm: trimmed,
        pageNumber: 1,
        pageSize: PAGE_SIZE,
        ...PARAMS_PRODUCTO_OPERATIVO,
      });

      if (requestId !== searchRequestIdRef.current) return;

      const items: any[] = res.items ?? [];
      const enriched = await enriquecerProductos(items);

      if (requestId !== searchRequestIdRef.current) return;

      setTotalCount(res.totalCount ?? enriched.length);
      setSuggestions(enriched);
      setOpen(enriched.length > 0);
      setHighlightedIndex(enriched.length > 0 ? 0 : -1);
      itemRefs.current = [];
    } catch {
      if (requestId !== searchRequestIdRef.current) return;
      resetBusqueda();
    } finally {
      if (requestId === searchRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [enriquecerProductos, resetBusqueda]);

  const cargarMas = useCallback(async () => {
    const trimmed = term.trim();
    if (!trimmed || loading || loadingMore) return;
    if (suggestions.length >= totalCount) return;

    const nextPage = pageNumber + 1;
    const requestId = searchRequestIdRef.current;
    setLoadingMore(true);

    try {
      const res = await productoService.getProductos({
        searchTerm: trimmed,
        pageNumber: nextPage,
        pageSize: PAGE_SIZE,
        ...PARAMS_PRODUCTO_OPERATIVO,
      });

      if (requestId !== searchRequestIdRef.current) return;

      const items: any[] = res.items ?? [];
      if (items.length === 0) return;

      const enriched = await enriquecerProductos(items);

      if (requestId !== searchRequestIdRef.current) return;

      setTotalCount(res.totalCount ?? totalCount);
      setPageNumber(nextPage);
      setSuggestions((prev) => {
        const existing = new Set(prev.map((s) => s.codigoArticulo.trim()));
        const nuevos = enriched.filter((s) => !existing.has(s.codigoArticulo.trim()));
        return [...prev, ...nuevos];
      });
      setOpen(true);
    } catch {
      /* mantener resultados actuales */
    } finally {
      if (requestId === searchRequestIdRef.current) {
        setLoadingMore(false);
      }
    }
  }, [term, loading, loadingMore, suggestions.length, totalCount, pageNumber, enriquecerProductos]);

  const hayMasResultados = suggestions.length < totalCount;

  const handleListScroll = useCallback(() => {
    const el = listScrollRef.current;
    if (!el || loading || loadingMore || !hayMasResultados) return;

    const threshold = 48;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
    if (nearBottom) void cargarMas();
  }, [loading, loadingMore, hayMasResultados, cargarMas]);

  useEffect(() => {
    if (!open || loading || loadingMore || !hayMasResultados) return;
    const el = listScrollRef.current;
    if (!el) return;
    if (el.scrollHeight <= el.clientHeight + 1) void cargarMas();
  }, [open, loading, loadingMore, hayMasResultados, suggestions.length, cargarMas]);

  useEffect(() => {
    if (highlightedIndex < 0 || loading || loadingMore || !hayMasResultados) return;
    if (highlightedIndex >= suggestions.length - 3) void cargarMas();
  }, [highlightedIndex, suggestions.length, loading, loadingMore, hayMasResultados, cargarMas]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => buscarProductos(term), 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [term, buscarProductos]);

  useEffect(() => {
    if (highlightedIndex >= 0) {
      itemRefs.current[highlightedIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (item: SuggestionItem) => {
    onAgregarProducto(item);
    setTerm('');
    setBarcodeError(null);
    resetBusqueda();
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const resolverYElegir = useCallback(async (codigo: string) => {
    setLoading(true);
    setBarcodeError(null);
    setOpen(false);
    try {
      const resolved = await productoService.resolverCodigoBarra(codigo, nroLista);
      let stock: number | null = null;
      try {
        const stockData = await inventarioInformes.getStockPorDeposito({
          codigoArticulo: resolved.codigoArticulo,
          pageSize: 50,
        });
        const rows: any[] = (stockData?.data ?? []).filter(
          (r: any) => (r.codigoArticulo ?? '').trim() === resolved.codigoArticulo.trim(),
        );
        if (rows.length > 0) {
          stock = rows.reduce(
            (acc: number, r: any) => acc + (Number(r.disponibleDeposito) || 0),
            0,
          );
        }
      } catch {
        /* sin stock */
      }

      const multiplicador = preciosConIva
        ? multiplicadorIvaProducto(resolved.producto, tipoIvaEmision)
        : 1;

      onAgregarProducto({
        codigoArticulo: resolved.codigoArticulo,
        descripcion: resolved.descripcion,
        precioUnitario: resolved.precioUnitario,
        precioUnitarioDisplay: preciosConIva
          ? aPrecioDisplay(resolved.precioUnitario, multiplicador)
          : undefined,
        cantidad: resolved.cantidad,
        um: resolved.um ?? '',
        stockDisponible: stock,
        productoIva: resolved.producto,
      });
      setTerm('');
      resetBusqueda();
      setTimeout(() => inputRef.current?.focus(), 50);
    } catch (err) {
      setBarcodeError(getApiErrorMessage(err, 'No se pudo resolver el código de barras.'));
    } finally {
      setLoading(false);
    }
  }, [nroLista, onAgregarProducto, preciosConIva, resetBusqueda, tipoIvaEmision]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      setHighlightedIndex(-1);
      return;
    }

    if (e.key === 'Enter') {
      const trimmed = term.trim();
      if (esEntradaCodigoBarra(trimmed)) {
        e.preventDefault();
        void resolverYElegir(trimmed);
        return;
      }
    }

    if (suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlightedIndex((prev) => {
        if (prev < 0) return 0;
        return prev < suggestions.length - 1 ? prev + 1 : 0;
      });
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setHighlightedIndex((prev) => {
        if (prev < 0) return suggestions.length - 1;
        return prev > 0 ? prev - 1 : suggestions.length - 1;
      });
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      const idx = highlightedIndex >= 0 ? highlightedIndex : 0;
      const item = suggestions[idx];
      if (item) handleSelect(item);
    }
  };

  const handleClear = () => {
    setTerm('');
    setBarcodeError(null);
    resetBusqueda();
    inputRef.current?.focus();
  };

  return (
    <Box ref={containerRef} sx={{ position: 'relative' }}>
      <TextField
        inputRef={inputRef}
        fullWidth
        placeholder="Código o descripción… ↑↓ y Enter"
        value={term}
        onChange={(e) => {
          setTerm(e.target.value);
          if (barcodeError) setBarcodeError(null);
        }}
        onKeyDown={handleKeyDown}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        disabled={disabled}
        size="small"
        error={Boolean(barcodeError)}
        helperText={barcodeError ?? undefined}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {loading ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" color="action" />}
            </InputAdornment>
          ),
          endAdornment: term ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={handleClear} edge="end">
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}
      />

      {open && suggestions.length > 0 && (
        <Paper
          ref={listScrollRef}
          onScroll={handleListScroll}
          elevation={6}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1400,
            maxHeight: 340,
            overflowY: 'auto',
            mt: 0.5,
            borderRadius: 2,
          }}
        >
          <List dense disablePadding>
            {suggestions.map((s, idx) => (
              <ListItem key={s.codigoArticulo} disablePadding divider={idx < suggestions.length - 1 || loadingMore}>
                <ListItemButton
                  ref={(el) => {
                    itemRefs.current[idx] = el;
                  }}
                  selected={idx === highlightedIndex}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                  onClick={() => handleSelect(s)}
                  sx={{
                    py: 0.75,
                    px: 1.5,
                    display: 'flex',
                    alignItems: 'stretch',
                    gap: 1.25,
                  }}
                >
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      flexShrink: 0,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'grey.200',
                      bgcolor: 'grey.50',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      component="img"
                      src={s.imageUrl ?? getProductoFallbackImage()}
                      alt=""
                      sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      onError={(e) => {
                        e.currentTarget.src = getProductoFallbackImage();
                      }}
                    />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <Tooltip
                      title={s.descripcion}
                      placement="top-start"
                      enterDelay={400}
                      leaveDelay={0}
                      disableInteractive
                    >
                      <Typography variant="body2" component="span" sx={descripcionSx}>
                        {s.descripcion}
                      </Typography>
                    </Tooltip>
                    <Box display="flex" alignItems="center" gap={1} mt={0.35} flexWrap="wrap">
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontFamily: 'monospace', flexShrink: 0, minWidth: 48 }}
                      >
                        {s.codigoArticulo}
                      </Typography>
                      <Typography variant="caption" color="primary.main" fontWeight={600}>
                        {preciosConIva ? 'Precio Final: ' : ''}
                        {formatCurrency(s.precioUnitarioDisplay ?? s.precioUnitario)}
                        {preciosConIva ? ' c/imp.' : ''}
                      </Typography>
                      {s.um && (
                        <Typography variant="caption" color="text.secondary">
                          / {s.um}
                        </Typography>
                      )}
                      <StockBadge stock={s.stockDisponible} />
                    </Box>
                  </Box>
                </ListItemButton>
              </ListItem>
            ))}
            {loadingMore && (
              <ListItem disablePadding>
                <Box sx={{ py: 1.25, display: 'flex', justifyContent: 'center', width: '100%' }}>
                  <CircularProgress size={18} />
                </Box>
              </ListItem>
            )}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default FacturaPOSSearchBar;
